import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { MdSearch, MdClose } from 'react-icons/md';
import { sortSearchResults } from 'utils/graphSearchUtils';
import styles from './GraphSearchControl.module.css';

const GraphSearchControl = ({ searchableNodes, onSelect, placeholder = "Find in graph..." }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Filter results when debouncedQuery changes
  useEffect(() => {
    if (debouncedQuery.trim().length === 0) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const lowerQuery = debouncedQuery.toLowerCase();
    const filtered = searchableNodes.filter(node => {
      if (node.haystack) {
        return node.haystack.includes(lowerQuery);
      }
      // Fallback for nodes without haystack (shouldn't happen with updated utils)
      return (node.label && node.label.toLowerCase().includes(lowerQuery)) ||
             (node.type && node.type.toLowerCase().includes(lowerQuery));
    });

    setResults(sortSearchResults(filtered, debouncedQuery).slice(0, 50));
    setIsOpen(true);
    setActiveIndex(-1);
  }, [debouncedQuery, searchableNodes]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (!isOpen && e.target.value) setIsOpen(true);
  };

  const handleSelect = (node) => {
    setQuery(node.label);
    setIsOpen(false);
    if (onSelect) {
      onSelect(node);
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || results.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) {
        handleSelect(results[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={styles.container} ref={wrapperRef}>
      <div className={styles.inputWrapper}>
        <MdSearch className={styles.searchIcon} />
        <input 
          ref={inputRef}
          type="text" 
          className={styles.input} 
          value={query} 
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          onFocus={() => { if (query) setIsOpen(true); }}
        />
        {query && (
          <MdClose className={styles.clearIcon} onClick={handleClear} />
        )}
      </div>
      {isOpen && results.length > 0 && (
        <ul className={styles.resultsList}>
          {results.map((result, index) => (
            <li 
              key={result.id} 
              onClick={() => handleSelect(result)} 
              className={`${styles.resultItem} ${index === activeIndex ? styles.active : ''}`}
            >
              <span className={styles.resultLabel}>{result.label}</span>
              <span className={result.isSuggested ? styles.resultTypeSuggested : result.isHidden ? styles.resultTypeHidden : styles.resultType}>{result.type}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

GraphSearchControl.propTypes = {
  searchableNodes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.string
  })).isRequired,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

export default GraphSearchControl;
